import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import Layout from '../components/layout';
import Title from '../components/title';
import './content.css';
import searchIcon from '../assets/search.svg';
import dropdownIcon from '../assets/dropdown.svg';
import editIcon from '../assets/edit.svg';

const CONTENT_PER_PAGE = 4;

const Content = () => {
  const [content, setContent] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState('All Sections');
  const [currentPage, setCurrentPage] = useState(1);

  const [id, setId] = useState('');
  const [editSection, setEditSection] = useState('');
  const [isEditContentOpen, setIsEditContentOpen] = useState(false);

  const [editTitlePart1En, setEditTitlePart1En] = useState('');
  const [editTitlePart1Ar, setEditTitlePart1Ar] = useState('');
  const [editTitlePart2En, setEditTitlePart2En] = useState('');
  const [editTitlePart2Ar, setEditTitlePart2Ar] = useState('');
  const [editSubtitleEn, setEditSubtitleEn] = useState('');
  const [editSubtitleAr, setEditSubtitleAr] = useState('');
  const [editDescriptionEn, setEditDescriptionEn] = useState('');
  const [editDescriptionAr, setEditDescriptionAr] = useState('');

  const [editCard1TitleEn, setEditCard1TitleEn] = useState('');
  const [editCard1TitleAr, setEditCard1TitleAr] = useState('');
  const [editCard1DescriptionEn, setEditCard1DescriptionEn] = useState('');
  const [editCard1DescriptionAr, setEditCard1DescriptionAr] = useState('');

  const [editCard2TitleEn, setEditCard2TitleEn] = useState('');
  const [editCard2TitleAr, setEditCard2TitleAr] = useState('');
  const [editCard2DescriptionEn, setEditCard2DescriptionEn] = useState('');
  const [editCard2DescriptionAr, setEditCard2DescriptionAr] = useState('');

  const [editCard3TitleEn, setEditCard3TitleEn] = useState('');
  const [editCard3TitleAr, setEditCard3TitleAr] = useState('');
  const [editCard3DescriptionEn, setEditCard3DescriptionEn] = useState('');
  const [editCard3DescriptionAr, setEditCard3DescriptionAr] = useState('');

  const fetchContent = async () => {
    const res = await supabase
      .from("content_management")
      .select("*");

    setContent(res.data);
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const editItem = (item) => {
    setId(item.id);
    setEditSection(item.section);

    const c = item.content || {};

    setEditTitlePart1En(c.title_part_1?.en || '');
    setEditTitlePart1Ar(c.title_part_1?.ar || '');
    setEditTitlePart2En(c.title_part_2?.en || '');
    setEditTitlePart2Ar(c.title_part_2?.ar || '');
    setEditSubtitleEn(c.subtitle?.en || '');
    setEditSubtitleAr(c.subtitle?.ar || '');
    setEditDescriptionEn(c.description?.en || '');
    setEditDescriptionAr(c.description?.ar || '');

    setEditCard1TitleEn(c.cards?.[0]?.title?.en || '');
    setEditCard1TitleAr(c.cards?.[0]?.title?.ar || '');
    setEditCard1DescriptionEn(c.cards?.[0]?.description?.en || '');
    setEditCard1DescriptionAr(c.cards?.[0]?.description?.ar || '');

    setEditCard2TitleEn(c.cards?.[1]?.title?.en || '');
    setEditCard2TitleAr(c.cards?.[1]?.title?.ar || '');
    setEditCard2DescriptionEn(c.cards?.[1]?.description?.en || '');
    setEditCard2DescriptionAr(c.cards?.[1]?.description?.ar || '');

    setEditCard3TitleEn(c.cards?.[2]?.title?.en || '');
    setEditCard3TitleAr(c.cards?.[2]?.title?.ar || '');
    setEditCard3DescriptionEn(c.cards?.[2]?.description?.en || '');
    setEditCard3DescriptionAr(c.cards?.[2]?.description?.ar || '');

    setIsEditContentOpen(true);
  };

  const updateContent = async (e) => {
    e.preventDefault();

    let finalContent = {
      title_part_1: { ar: editTitlePart1Ar, en: editTitlePart1En },
      title_part_2: { ar: editTitlePart2Ar, en: editTitlePart2En },
      subtitle: { ar: editSubtitleAr, en: editSubtitleEn },
      description: { ar: editDescriptionAr, en: editDescriptionEn }
    };

    if (editSection === 'how_it_works') {
      finalContent.cards = [
        {
          title: { ar: editCard1TitleAr, en: editCard1TitleEn },
          description: { ar: editCard1DescriptionAr, en: editCard1DescriptionEn }
        },
        {
          title: { ar: editCard2TitleAr, en: editCard2TitleEn },
          description: { ar: editCard2DescriptionAr, en: editCard2DescriptionEn }
        },
        {
          title: { ar: editCard3TitleAr, en: editCard3TitleEn },
          description: { ar: editCard3DescriptionAr, en: editCard3DescriptionEn }
        }
      ];
    }

    await supabase
      .from("content_management")
      .update({ content: finalContent })
      .eq('id', id);

    await fetchContent();
    setIsEditContentOpen(false);
  };

  const getPreview = (item) => {
    const parts = [];
    const c = item.content || {};

    const title1 = c.title_part_1?.en || c.title_part_1?.ar;
    const title2 = c.title_part_2?.en || c.title_part_2?.ar;

    if (title1) {
      parts.push(title1 + (title2 ? ' ' + title2 : ''));
    }

    const subtitle = c.subtitle?.en || c.subtitle?.ar;
    const desc = c.description?.en || c.description?.ar;

    if (subtitle) {
      parts.push(subtitle);
    } else if (desc) {
      parts.push(desc);
    }

    if (c.cards) {
      parts.push(c.cards.length + ' cards');
    }

    const result = parts.join(' · ');
    return result.length > 80 ? result.slice(0, 80) + '…' : result;
  };

  const sectionOptions = [...new Set(content.map((c) => c.section))];

  const filteredContent = content.filter((item) => {
    const value = searchTerm.toLowerCase();

    const matchesSection =
      selectedSection === 'All Sections' ||
      item.section === selectedSection;

    const matchesSearch = (
      item.id.toString().includes(value) ||
      item.section.toLowerCase().includes(value)
    );

    return matchesSearch && matchesSection;
  });

  const totalPages = Math.ceil(filteredContent.length / CONTENT_PER_PAGE);
  const startIndex = (currentPage - 1) * CONTENT_PER_PAGE;
  const paginatedContent = filteredContent.slice(startIndex, startIndex + CONTENT_PER_PAGE);

  const renderBasicFields = () => (
    <>
      <label className="content_modal_field">
        <span>Title Part 1 (English)</span>
        <input
          onChange={(e) => setEditTitlePart1En(e.target.value)}
          type="text"
          value={editTitlePart1En}
        />
      </label>

      <label className="content_modal_field">
        <span>Title Part 1 (Arabic)</span>
        <input
          dir="rtl"
          onChange={(e) => setEditTitlePart1Ar(e.target.value)}
          type="text"
          value={editTitlePart1Ar}
        />
      </label>

      <label className="content_modal_field">
        <span>Title Part 2 (English)</span>
        <input
          onChange={(e) => setEditTitlePart2En(e.target.value)}
          type="text"
          value={editTitlePart2En}
        />
      </label>

      <label className="content_modal_field">
        <span>Title Part 2 (Arabic)</span>
        <input
          dir="rtl"
          onChange={(e) => setEditTitlePart2Ar(e.target.value)}
          type="text"
          value={editTitlePart2Ar}
        />
      </label>

      <label className="content_modal_field">
        <span>Subtitle (English)</span>
        <input
          onChange={(e) => setEditSubtitleEn(e.target.value)}
          type="text"
          value={editSubtitleEn}
        />
      </label>

      <label className="content_modal_field">
        <span>Subtitle (Arabic)</span>
        <input
          dir="rtl"
          onChange={(e) => setEditSubtitleAr(e.target.value)}
          type="text"
          value={editSubtitleAr}
        />
      </label>

      <label className="content_modal_field content_modal_field_full">
        <span>Description (English)</span>
        <textarea
          onChange={(e) => setEditDescriptionEn(e.target.value)}
          rows={3}
          value={editDescriptionEn}
        />
      </label>

      <label className="content_modal_field content_modal_field_full">
        <span>Description (Arabic)</span>
        <textarea
          dir="rtl"
          onChange={(e) => setEditDescriptionAr(e.target.value)}
          rows={3}
          value={editDescriptionAr}
        />
      </label>
    </>
  );

  const renderHowItWorksCards = () => (
    <>
      <div className="content_modal_card_group">
        <div className="content_modal_card_label">Card 1</div>
        <div className="content_modal_grid">
          <label className="content_modal_field">
            <span>Title (English)</span>
            <input
              onChange={(e) => setEditCard1TitleEn(e.target.value)}
              type="text"
              value={editCard1TitleEn}
            />
          </label>
          <label className="content_modal_field">
            <span>Title (Arabic)</span>
            <input
              dir="rtl"
              onChange={(e) => setEditCard1TitleAr(e.target.value)}
              type="text"
              value={editCard1TitleAr}
            />
          </label>
          <label className="content_modal_field content_modal_field_full">
            <span>Description (English)</span>
            <textarea
              onChange={(e) => setEditCard1DescriptionEn(e.target.value)}
              rows={2}
              value={editCard1DescriptionEn}
            />
          </label>
          <label className="content_modal_field content_modal_field_full">
            <span>Description (Arabic)</span>
            <textarea
              dir="rtl"
              onChange={(e) => setEditCard1DescriptionAr(e.target.value)}
              rows={2}
              value={editCard1DescriptionAr}
            />
          </label>
        </div>
      </div>

      <div className="content_modal_card_group">
        <div className="content_modal_card_label">Card 2</div>
        <div className="content_modal_grid">
          <label className="content_modal_field">
            <span>Title (English)</span>
            <input
              onChange={(e) => setEditCard2TitleEn(e.target.value)}
              type="text"
              value={editCard2TitleEn}
            />
          </label>
          <label className="content_modal_field">
            <span>Title (Arabic)</span>
            <input
              dir="rtl"
              onChange={(e) => setEditCard2TitleAr(e.target.value)}
              type="text"
              value={editCard2TitleAr}
            />
          </label>
          <label className="content_modal_field content_modal_field_full">
            <span>Description (English)</span>
            <textarea
              onChange={(e) => setEditCard2DescriptionEn(e.target.value)}
              rows={2}
              value={editCard2DescriptionEn}
            />
          </label>
          <label className="content_modal_field content_modal_field_full">
            <span>Description (Arabic)</span>
            <textarea
              dir="rtl"
              onChange={(e) => setEditCard2DescriptionAr(e.target.value)}
              rows={2}
              value={editCard2DescriptionAr}
            />
          </label>
        </div>
      </div>

      <div className="content_modal_card_group">
        <div className="content_modal_card_label">Card 3</div>
        <div className="content_modal_grid">
          <label className="content_modal_field">
            <span>Title (English)</span>
            <input
              onChange={(e) => setEditCard3TitleEn(e.target.value)}
              type="text"
              value={editCard3TitleEn}
            />
          </label>
          <label className="content_modal_field">
            <span>Title (Arabic)</span>
            <input
              dir="rtl"
              onChange={(e) => setEditCard3TitleAr(e.target.value)}
              type="text"
              value={editCard3TitleAr}
            />
          </label>
          <label className="content_modal_field content_modal_field_full">
            <span>Description (English)</span>
            <textarea
              onChange={(e) => setEditCard3DescriptionEn(e.target.value)}
              rows={2}
              value={editCard3DescriptionEn}
            />
          </label>
          <label className="content_modal_field content_modal_field_full">
            <span>Description (Arabic)</span>
            <textarea
              dir="rtl"
              onChange={(e) => setEditCard3DescriptionAr(e.target.value)}
              rows={2}
              value={editCard3DescriptionAr}
            />
          </label>
        </div>
      </div>
    </>
  );

  const renderFields = () => {
    if (editSection === 'how_it_works') {
      return (
        <>
          {renderBasicFields()}
          {renderHowItWorksCards()}
        </>
      );
    }

    return renderBasicFields();
  };

  const sectionLabels = {
    hero: 'Hero',
    events: 'Events',
    how_it_works: 'How It Works',
    booking: 'Booking',
    community: 'Community',
    newsletter: 'Newsletter',
  };

  return (
    <Layout>
      <div className="content_page">
        <Title
          subtitle="Manage all content sections on the Eventra platform"
          title="CONTENT MANAGEMENT"
        />

        <div className="content_controls_card">
          <div className="content_controls_left">
            <label className="content_search" htmlFor="content-search">
              <img alt="" src={searchIcon} />
              <input
                id="content-search"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search content..."
                type="text"
                value={searchTerm}
              />
            </label>

            <div className="content_filter">
              <select
                onChange={(e) => {
                  setSelectedSection(e.target.value);
                  setCurrentPage(1);
                }}
                value={selectedSection}
              >
                <option value="All Sections">All Sections</option>
                {sectionOptions.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
              <img alt="" src={dropdownIcon} />
            </div>
          </div>
        </div>

        <div className="content_table_card">
          <div className="content_table_top">
            <div className="content_table_title">All Content</div>
            <div className="content_table_count">{filteredContent.length} total sections</div>
          </div>

          <div className="content_table_scroll">
            <div className="content_row content_table_head">
              <div className="content_checkbox">
                <input type="checkbox" />
              </div>
              <div>ID</div>
              <div>Section</div>
              <div>Preview</div>
              <div>Updated At</div>
              <div>Actions</div>
            </div>

            <div className="content_table_body">
              {paginatedContent.map((item) => (
                <div className="content_row" key={item.id}>
                  <div className="content_checkbox">
                    <input type="checkbox" />
                  </div>

                  <div className="content_cell_text">{item.id}</div>
                  <div className="content_cell_text">{item.section}</div>
                  <div className="content_cell_text content_cell_preview_text">
                    {getPreview(item)}
                  </div>
                  <div className="content_cell_text">{item.updated_at}</div>

                  <div className="content_actions">
                    <button
                      className="content_action_button"
                      onClick={() => editItem(item)}
                    >
                      <img alt="" src={editIcon} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="content_pagination">
              <button
                className="content_pagination_button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <div className="content_pagination_info">
                Page {currentPage} of {totalPages}
              </div>
              <button
                className="content_pagination_button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {isEditContentOpen && (
          <div
            className="content_modal_overlay"
            onClick={() => setIsEditContentOpen(false)}
          >
            <div
              className="content_modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="content_modal_header">
                <div className="content_modal_heading">
                  <h2>EDIT {sectionLabels[editSection].toUpperCase()}</h2>
                  <p>Update the content for the {sectionLabels[editSection]} section below.</p>
                </div>

                <button
                  className="content_modal_close"
                  onClick={() => setIsEditContentOpen(false)}
                >
                  x
                </button>
              </div>

              <form
                className="content_modal_form"
                onSubmit={updateContent}
              >
                <div className="content_modal_grid">
                  {renderFields()}
                </div>

                <div className="content_modal_actions">
                  <button
                    className="content_modal_secondary_button"
                    onClick={() => setIsEditContentOpen(false)}
                  >
                    Cancel
                  </button>
                  <button className="content_modal_primary_button">
                    Update Content
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Content;
